import { c as _c } from "react/compiler-runtime";
import { useQueryStates } from 'nuqs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { type CumulativeType, expensesMetricChartFiltersConfig } from '../../lib/expenses-metric-chart-search-params';
import { parseAnalyticsToSeriesExpensesMetricChart } from '../../lib/expenses-metric-chart-utils';
import { type Budget, type ExpensesMetricBudgetAnalytic, GRANULARITY_OPTIONS } from '../../types';
import type { EChartsOption } from 'echarts-for-react';
interface ExpensesMetricChartProps {
  budgetsAnalytics: ExpensesMetricBudgetAnalytic | undefined;
  budgets: Budget[];
  allBudgets: Budget[];
  codePath: string;
}
function useExpensesMetricChart(t0) {
  const $ = _c(47);
  const {
    budgetsAnalytics,
    budgets,
    allBudgets,
    codePath
  } = t0;
  const refExpensesMetricChart = useRef(null);
  const [filters, setFilters] = useQueryStates(expensesMetricChartFiltersConfig);
  const selectedGranularity = filters.granularity;
  const isCumulative = filters.cumulative === "true";
  const cumulativeType = filters.cumulativeType;
  const [isChecked, setIsChecked] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      to: "sm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isMobile = useMediaQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [inactiveSeries, setInactiveSeries] = useState(t2);
  let t3;
  if ($[2] !== codePath) {
    t3 = codePath.split("/");
    $[2] = codePath;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const levelNumber = t3.length;
  let t4;
  if ($[4] !== allBudgets || $[5] !== budgets || $[6] !== budgetsAnalytics || $[7] !== cumulativeType || $[8] !== isCumulative) {
    t4 = parseAnalyticsToSeriesExpensesMetricChart(budgetsAnalytics, budgets, allBudgets, {
      isCumulative,
      cumulativeType
    });
    $[4] = allBudgets;
    $[5] = budgets;
    $[6] = budgetsAnalytics;
    $[7] = cumulativeType;
    $[8] = isCumulative;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const allSeries = t4;
  let t5;
  if ($[10] !== allSeries || $[11] !== inactiveSeries) {
    let t6;
    if ($[13] !== inactiveSeries) {
      t6 = item => {
        if (inactiveSeries.includes(item.name)) {
          return {
            ...item,
            isVisible: false,
            itemStyle: {
              ...item.itemStyle,
              color: "#ccc",
              colorOriginal: "#ccc"
            },
            lineStyle: {
              ...item.lineStyle,
              color: "#ccc",
              colorOriginal: "#ccc"
            },
            data: item.data.map(_temp)
          };
        }
        return {
          ...item,
          lineStyle: {
            ...item.lineStyle,
            width: 2,
            opacity: 1
          },
          itemStyle: {
            ...item.itemStyle,
            opacity: 1
          }
        };
      };
      $[13] = inactiveSeries;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = allSeries.map(t6);
    $[10] = allSeries;
    $[11] = inactiveSeries;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const parsedSeries = t5;
  const series = parsedSeries;
  let t6;
  if ($[15] !== setFilters) {
    t6 = granularity => {
      setFilters({
        granularity
      });
    };
    $[15] = setFilters;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleSelectGranularity = t6;
  let t7;
  if ($[17] !== setFilters) {
    t7 = value => {
      setFilters({
        cumulative: "true",
        cumulativeType: value
      });
    };
    $[17] = setFilters;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleChangeCumulativeType = t7;
  let t8;
  if ($[19] !== isCumulative || $[20] !== setFilters) {
    t8 = () => {
      setFilters({
        cumulative: isCumulative ? "false" : "true",
        cumulativeType: "relative"
      });
    };
    $[19] = isCumulative;
    $[20] = setFilters;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleToggleCumulative = t8;
  let t9;
  if ($[22] !== setFilters) {
    t9 = () => {
      setFilters({
        granularity: GRANULARITY_OPTIONS.Monthly,
        cumulative: "false",
        cumulativeType: "relative"
      });
    };
    $[22] = setFilters;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const onReset = t9;
  const isResetDisabled = selectedGranularity === GRANULARITY_OPTIONS.Monthly && !isCumulative && cumulativeType === "relative";
  let t10;
  if ($[24] !== inactiveSeries) {
    t10 = toggleSeries => {
      setInactiveSeries(inactiveSeries.includes(toggleSeries) ? inactiveSeries.filter(series_0 => series_0 !== toggleSeries) : [...inactiveSeries, toggleSeries]);
    };
    $[24] = inactiveSeries;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleToggleSeries = t10;
  let t11;
  if ($[26] !== allSeries || $[27] !== inactiveSeries || $[28] !== isChecked) {
    t11 = () => {
      if (!isChecked && inactiveSeries.length > 0) {
        setInactiveSeries([]);
      } else {
        setInactiveSeries([...allSeries.map(_temp2)]);
      }
      setIsChecked(!isChecked);
    };
    $[26] = allSeries;
    $[27] = inactiveSeries;
    $[28] = isChecked;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const handleChangeSwitch = t11;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = legendName => {
      const chartInstance = refExpensesMetricChart.current?.getEchartsInstance();
      chartInstance?.dispatchAction({
        type: "highlight",
        seriesName: legendName
      });
    };
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const onLegendItemHover = t12;
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = legendName_0 => {
      const chartInstance_0 = refExpensesMetricChart.current?.getEchartsInstance();
      chartInstance_0?.dispatchAction({
        type: "downplay",
        seriesName: legendName_0
      });
    };
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const onLegendItemLeave = t13;
  const showScrollAndToggle = isMobile ? series.length > 6 : series.length > 8;
  const showLegendValue = levelNumber <= 2;
  let t14;
  if ($[32] !== cumulativeType || $[33] !== handleChangeCumulativeType || $[34] !== handleChangeSwitch || $[35] !== handleSelectGranularity || $[36] !== handleToggleCumulative || $[37] !== handleToggleSeries || $[38] !== isChecked || $[39] !== isCumulative || $[40] !== isResetDisabled || $[41] !== onReset || $[42] !== selectedGranularity || $[43] !== series || $[44] !== showLegendValue || $[45] !== showScrollAndToggle) {
    t14 = {
      series,
      showScrollAndToggle,
      handleToggleSeries,
      handleChangeSwitch,
      isChecked,
      onLegendItemHover,
      onLegendItemLeave,
      refExpensesMetricChart,
      showLegendValue,
      selectedGranularity,
      isCumulative,
      cumulativeType,
      handleSelectGranularity,
      handleChangeCumulativeType,
      handleToggleCumulative,
      onReset,
      isResetDisabled
    };
    $[32] = cumulativeType;
    $[33] = handleChangeCumulativeType;
    $[34] = handleChangeSwitch;
    $[35] = handleSelectGranularity;
    $[36] = handleToggleCumulative;
    $[37] = handleToggleSeries;
    $[38] = isChecked;
    $[39] = isCumulative;
    $[40] = isResetDisabled;
    $[41] = onReset;
    $[42] = selectedGranularity;
    $[43] = series;
    $[44] = showLegendValue;
    $[45] = showScrollAndToggle;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
}
function _temp2(series_1) {
  return series_1.name;
}
function _temp(point) {
  return {
    ...point,
    value: null
  };
}
export { useExpensesMetricChart };